import { useOwlStore } from "@swisstiming/owl-core";
import { getQueryParamDictionary, MODES } from "@swisstiming/webtec-kit";
import { AfterFetchContext, useFetch } from "@vueuse/core";
import { isNil } from "lodash-es";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export const useArchiveStore = defineStore("ArchiveStore", () => {
    const owlStore = useOwlStore();

    const DATA_URL_DEV = "https://stdevwdlatharchivewesteu.z6.web.core.windows.net";
    const DATA_URL_UAT = "https://stuatwdlatharchivewesteu.z6.web.core.windows.net";
    const DATA_URL_PROD = "https://ath-wdl-archive.azureedge.net";

    const baseUrl = computed(() => {
        if (owlStore.config.mode === MODES.PROD || owlStore.config.mode === MODES.PROD_SHORT) {
            return DATA_URL_PROD;
        }

        if (owlStore.config.mode === MODES.UAT) {
            return DATA_URL_UAT;
        }

        if (
            import.meta.env.MODE === "development" ||
            owlStore.config.mode === MODES.DEV ||
            owlStore.config.mode === MODES.DEV_SHORT
        ) {
            return DATA_URL_DEV;
        }

        return DATA_URL_PROD;
    });

    enum WDL_ARCHIVE_QUERY_NAMES {
        DEFAULT_MEETING = "default_meeting"
    }
    const qParams = computed(() => getQueryParamDictionary<WDL_ARCHIVE_QUERY_NAMES>());

    const seasonsJsonUrl = computed(() => `${baseUrl.value}/seasons.json`);

    // and empty string as url-parameter for 'useFetch' will not trigger a request
    const getMeetingsJsonUrl = (season?: string | number) =>
        isNil(season) ? "" : `${baseUrl.value}/${season}/meetings.json`;

    const getMeetingJsonUrl = (season?: string | number, meeting?: string) =>
        isNil(season) || isNil(meeting) ? "" : `${baseUrl.value}/${season}/${meeting}.json`;

    const getDiamondRaceJsonUrl = (season?: string | number) =>
        isNil(season) ? "" : `${baseUrl.value}/${season}/diamond_race.json`;

    const getQualifierJsonUrl = (season?: string | number) =>
        isNil(season) ? "" : `${baseUrl.value}/${season}/qualifier.json`;

    const getPdfUrl = (season: string | number, meeting: string, pdfFileName: string) =>
        `${baseUrl.value}/${season}/${meeting}/${pdfFileName}`;

    const useSeasons = () => {
        const selectedSeason = ref<number>();

        const {
            data: seasonsData,
            isFetching,
            isFinished
        } = useFetch(seasonsJsonUrl, {
            refetch: true,
            afterFetch(ctx: AfterFetchContext<number[]>) {
                // preselect latest season as default
                selectedSeason.value = ctx.data?.at(-1);
                return ctx;
            }
        }).json<string[]>();

        return { seasonsData, selectedSeason, isFetching, isFinished };
    };

    return {
        baseUrl,
        seasonsJsonUrl,
        getMeetingsJsonUrl,
        getMeetingJsonUrl,
        getDiamondRaceJsonUrl,
        getQualifierJsonUrl,
        getPdfUrl,
        useSeasons,
        qParams
    };
});
